import { ROLE_PERMISSIONS } from './permissions';
import { type RoleType, STAFF_ROLES } from './roles';
import { SCOPE_SUFFIXES } from './scopes';

/**
 * Strip a known scope suffix from a permission.
 * e.g. 'client:read_own' → 'client:read'. Returns null if no suffix.
 * Mirrors BE `getBasePermission`.
 */
function getBasePermission(perm: string): string | null {
  for (const suffix of SCOPE_SUFFIXES) {
    if (perm.endsWith(suffix)) {
      return perm.slice(0, -suffix.length);
    }
  }
  return null;
}

/**
 * Check whether a single role has a permission.
 * Mirrors BE `hasPermission` semantics:
 *   1. '*' wildcard → always true
 *   2. exact match → true
 *   3. 'module:*' wildcard → true
 *   4. scoped variant exists (e.g. 'client:read_own' satisfies 'client:read')
 *
 * `role` is `null` when the user has no UserRole row (defensive — BE blocks
 * login for that case, but /auth/me may surface it). A null role grants
 * nothing.
 */
export function hasPermission(role: RoleType | null, permission: string): boolean {
  if (!role) return false;
  // Widen the narrow literal tuple type so `.includes(arbitrary string)` typechecks.
  const perms: readonly string[] = ROLE_PERMISSIONS[role];

  if (perms.includes('*')) return true;
  if (perms.includes(permission)) return true;

  const colonIdx = permission.indexOf(':');
  if (colonIdx > 0) {
    const module = permission.slice(0, colonIdx);
    if (perms.includes(`${module}:*`)) return true;
  }

  // Scoped variant: if the gate requires 'client:read', accept 'client:read_own' etc.
  return perms.some((p) => getBasePermission(p) === permission);
}

/** True if any permission in the list passes. */
export function hasAnyPermission(role: RoleType | null, permissions: string[]): boolean {
  return permissions.some((p) => hasPermission(role, p));
}

/** True if the user has a staff role. */
export function isStaffRole(role: RoleType | null): boolean {
  return role !== null && STAFF_ROLES.includes(role);
}

/** True if the user has the client role. */
export function isClientRole(role: RoleType | null): boolean {
  return role === 'client';
}
